import * as v from "@badrap/valita";
import * as cbor from "cbor-x";
import { base64, base64url } from "rfc4648";
import { TvrnLocalStorage } from "#app/misc/storage.ts";
import { RobustWebSocket } from "#app/utils/robust-web-socket.ts";
import type {
  AccountMeta,
  RecoverAccountRequest,
  UpdateAccountRequest,
} from "#core/schemas/account.ts";
import { $Menu, type Menu } from "#core/schemas/menu.ts";
import {
  $Order,
  $OrderId,
  type Order,
  type OrderId,
  type OrderNew,
} from "#core/schemas/order.ts";
import {
  $Product,
  $ProductId,
  type Product,
  type ProductId,
  type ProductNew,
} from "#core/schemas/product.ts";
import type { TransactionNew } from "#core/schemas/transaction.ts";
import { $Transaction, type Transaction } from "#core/schemas/transaction.ts";
import { Token } from "#core/token.ts";
import { assert } from "#core/utils/assert.ts";

export type ClientEventMap = {
  "tvrn.authorized": Event;
  "tvrn.deauthorized": Event;
  "tvrn.stale.menu": Event;
  "tvrn.stale.orders": Event;
  "tvrn.stale.transactions": Event;
};

const STORAGE_KEY_TOKEN = "token";

export class TvrnService extends EventTarget {
  #token: string | null = null;
  #localStorage: TvrnLocalStorage;

  constructor(storage: TvrnLocalStorage = TvrnLocalStorage.BROWSER) {
    super();

    this.#localStorage = storage;

    this.addEventListener("tvrn.authorized", () => {
      assert(this.#token !== null);

      this.#localStorage.set(STORAGE_KEY_TOKEN, this.#token);
    });

    this.addEventListener("tvrn.deauthorized", () => {
      this.#localStorage.remove(STORAGE_KEY_TOKEN);
    });

    void this.#localStorage.get(STORAGE_KEY_TOKEN).then(
      (token) => {
        console.debug("restoring token from storage");
        this.#token = token;
      },
      (error) => {
        console.warn("unable to restore token", error);
      },
    );
  }

  override addEventListener: {
    <K extends keyof ClientEventMap>(
      type: K,
      listener: (ev: ClientEventMap[K]) => unknown,
      options?: boolean | AddEventListenerOptions,
    ): void;
    (
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | AddEventListenerOptions,
    ): void;
  } = super.addEventListener;

  get localStorage(): TvrnLocalStorage {
    return this.#localStorage;
  }

  get authorized(): boolean {
    return this.#token !== null;
  }

  get user(): string | null {
    if (this.#token === null) {
      return null;
    }

    const payload = this.#token.split(".", 1)[0];

    assert(typeof payload === "string");

    return v
      .object({
        sub: v.string(),
      })
      .parse(cbor.decode(base64url.parse(payload, { loose: true })), {
        mode: "strip",
      }).sub;
  }

  async signIn(
    { siret, password }: { siret: string; password: string },
    options?: RequestInit,
  ): Promise<undefined | Response> {
    const passwordB64 = base64.stringify(new TextEncoder().encode(password));

    const response = await fetch(
      `/api/accounts/${encodeURIComponent(siret)}/token`,
      {
        method: "POST",
        ...options,
        headers: new Headers([
          ["Authorization", `Password ${passwordB64}`],
          ...new Headers(options?.headers).entries(),
        ]),
      },
    );

    if (response.ok) {
      this.#token = v.string().parse(await response.json());
      this.dispatchEvent(new Event("tvrn.authorized"));

      return;
    }

    return response;
  }

  async signUp(
    {
      siret,
      email,
      password,
    }: {
      siret: string;
      email: string;
      password: string;
    },
    options?: RequestInit,
  ): Promise<Response> {
    return await fetch(`/api/accounts/${encodeURIComponent(siret)}`, {
      method: "POST",
      body: JSON.stringify({ email, password }),
      ...options,
      headers: new Headers([
        ["Content-Type", "application/json"],
        ...new Headers(options?.headers).entries(),
      ]),
    });
  }

  async recoverAccount(
    { password, token }: { password: string; token: string },
    options?: RequestInit,
  ): Promise<Response> {
    const { sub: siret } = await Token.subtle
      .intoParts(token)
      .then(({ payload }) => Token.subtle.parsePayload(payload));

    return await fetch(`/api/accounts/${encodeURIComponent(siret)}/recovery`, {
      method: "PATCH",
      body: JSON.stringify({
        password,
      } satisfies RecoverAccountRequest),
      ...options,
      headers: new Headers([
        ["Content-Type", "application/json"],
        ["Authorization", `Bearer ${token}`],
        ...new Headers(options?.headers).entries(),
      ]),
    });
  }

  async updateAccount(
    data: UpdateAccountRequest,
    options?: RequestInit,
  ): Promise<Response> {
    if (this.user === null) {
      throw new Error("not authenticated");
    }

    return await this.fetchAuthorized(
      `/api/accounts/${encodeURIComponent(this.user)}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
        ...options,
        headers: new Headers([
          ["Content-Type", "application/json"],
          ...new Headers(options?.headers).entries(),
        ]),
      },
    );
  }

  async signOut(): Promise<void> {
    if (this.#token !== null) {
      this.#token = null;
      this.dispatchEvent(new Event("tvrn.deauthorized"));
    }
  }

  async resetPassword(siret: string, options?: RequestInit): Promise<Response> {
    return await fetch(`/api/accounts/${encodeURIComponent(siret)}/recovery`, {
      method: "POST",
      ...options,
    });
  }

  async userMeta(): Promise<AccountMeta> {
    if (this.user === null) {
      throw new Error("not authenticated");
    }

    return await this.fetchAuthorized(
      `/api/accounts/${encodeURIComponent(this.user)}`,
    ).then((response) => response.json());
  }

  async fetchAuthorized(
    ...args: ConstructorParameters<typeof Request>
  ): Promise<Response> {
    if (this.#token === null) {
      throw new Error("no token");
    }

    const request = new Request(...args);

    request.headers.set("Authorization", `Bearer ${this.#token}`);

    return await fetch(request, { mode: "same-origin" }).then(
      async (response) => {
        if (!response.ok && response.status === 401) {
          await this.signOut();
        }

        return response;
      },
    );
  }

  subscribeAuthorized(url: string | URL): RobustWebSocket {
    if (this.#token === null) {
      throw new Error("no token");
    }

    return new RobustWebSocket(url, [
      "tvrn",
      `tvrn.token.${encodeURIComponent(this.#token)}`,
    ]);
  }

  async fetchMenu(options?: RequestInit): Promise<Menu | Response> {
    return await this.fetchAuthorized("/api/menu", {
      method: "GET",
      mode: "same-origin",
      ...options,
    }).then(async (response) =>
      response.ok ? $Menu.parse(await response.json()) : response,
    );
  }

  async fetchProduct(
    id: ProductId,
    options?: RequestInit,
  ): Promise<Product | Response> {
    return await this.fetchAuthorized(
      `/api/products/${encodeURIComponent(id)}`,
      {
        method: "GET",
        mode: "same-origin",
        ...options,
      },
    ).then(async (response) =>
      response.ok ? $Product.parse(await response.json()) : response,
    );
  }

  async fetchOrderList(options?: RequestInit): Promise<Order[] | Response> {
    return await this.fetchAuthorized("/api/orders", {
      method: "GET",
      mode: "same-origin",
      ...options,
    }).then(async (response) =>
      response.ok
        ? v
            .array($Order)
            .parse(await response.json())
            .sort((a, b) =>
              Temporal.ZonedDateTime.compare(a.datetime, b.datetime),
            )
        : response,
    );
  }

  async fetchOrder(
    id: OrderId,
    options?: RequestInit,
  ): Promise<Order | Response> {
    return await this.fetchAuthorized(`/api/orders/${encodeURIComponent(id)}`, {
      method: "GET",
      mode: "same-origin",
      ...options,
    }).then(async (response) =>
      response.ok ? $Order.parse(await response.json()) : response,
    );
  }

  async commitMenu(menu: Menu, options?: RequestInit): Promise<Response> {
    return this.fetchAuthorized("/api/menu", {
      method: "POST",
      mode: "same-origin",
      body: JSON.stringify(menu),
      ...options,
      headers: new Headers([
        ["Content-Type", "application/json"],
        ...new Headers(options?.headers).entries(),
      ]),
    }).finally(() => {
      this.dispatchEvent(new Event("tvrn.stale.menu"));
    });
  }

  async commitProduct(
    product: ProductNew,
    options?: RequestInit,
  ): Promise<ProductId | Response> {
    return this.fetchAuthorized("/api/products", {
      method: "POST",
      mode: "same-origin",
      body: JSON.stringify(product),
      ...options,
      headers: new Headers([
        ["Content-Type", "application/json"],
        ...new Headers(options?.headers).entries(),
      ]),
    }).then(async (response) =>
      response.ok ? $ProductId.parse(await response.json()) : response,
    );
  }

  async commitOrder(
    order: OrderNew,
    options?: RequestInit,
  ): Promise<OrderId | Response> {
    return await this.fetchAuthorized("/api/orders", {
      method: "POST",
      mode: "same-origin",
      body: JSON.stringify(order),
      ...options,
      headers: new Headers([
        ["Content-Type", "application/json"],
        ...new Headers(options?.headers).entries(),
      ]),
    })
      .then(async (response) =>
        response.ok ? $OrderId.parse(await response.json()) : response,
      )
      .finally(() => {
        this.dispatchEvent(new Event("tvrn.stale.orders"));
      });
  }

  async deleteOrder(id: OrderId, options?: RequestInit): Promise<Response> {
    return await this.fetchAuthorized(`/api/orders/${encodeURIComponent(id)}`, {
      method: "DELETE",
      mode: "same-origin",
      ...options,
    }).finally(() => {
      this.dispatchEvent(new Event("tvrn.stale.orders"));
    });
  }

  async fetchTransactionList(
    options?: RequestInit,
  ): Promise<Transaction[] | Response> {
    return await this.fetchAuthorized("/api/transactions", {
      method: "GET",
      mode: "same-origin",
      ...options,
    }).then(async (response) =>
      response.ok
        ? v
            .array($Transaction)
            .parse(await response.json())
            .sort((a, b) =>
              Temporal.ZonedDateTime.compare(a.datetime, b.datetime),
            )
        : response,
    );
  }

  async fetchTransaction(
    id: string,
    options?: RequestInit,
  ): Promise<Transaction | Response> {
    return await this.fetchAuthorized(
      `/api/transactions/${encodeURIComponent(id)}`,
      {
        method: "GET",
        mode: "same-origin",
        ...options,
      },
    ).then(async (response) =>
      response.ok ? $Transaction.parse(await response.json()) : response,
    );
  }

  async commitTransaction(
    transaction: TransactionNew,
    options?: RequestInit,
  ): Promise<Response> {
    return await this.fetchAuthorized("/api/transactions", {
      method: "POST",
      mode: "same-origin",
      body: JSON.stringify(transaction),
      ...options,
      headers: new Headers([
        ["Content-Type", "application/json"],
        ...new Headers(options?.headers).entries(),
      ]),
    }).finally(() => {
      this.dispatchEvent(new Event("tvrn.stale.transactions"));
    });
  }

  orderListSubscription(): RobustWebSocket {
    return this.subscribeAuthorized("/api/orders");
  }

  menuSubscription(): RobustWebSocket {
    return this.subscribeAuthorized("/api/menu");
  }

  transactionListSubscription(): RobustWebSocket {
    return this.subscribeAuthorized("/api/transactions");
  }
}
